import React from "react";
import { Star, Check, X, Info, RotateCw, Globe, MapPin, Clock, Tags, Camera, ExternalLink } from "lucide-react";

const PlacesV2Info = ({ data, onReset, showCardsOnly = false, showDetailsOnly = false }) => {
	// Check if we have any Places API v2 data
	const hasV2Data = data.website || data.rating || data.user_ratings_total ||
		data.open_now !== undefined || data.opening_hours || data.current_opening_hours ||
		data.business_status || data.types || data.primary_type ||
		(data.photos && data.photos.length > 0);

	if (!hasV2Data) return null;

	// If showing cards only (for sidebar)
	if (showCardsOnly) {
		return (
			<div className="idg-places-v2-info">
				<div className="idg-places-v2-grid">

					{/* Rating */}
					{data.rating && (
						<div className="idg-v2-card">
							<div className="idg-v2-icon"><Star size={18} /></div>
							<div className="idg-v2-content">
								<h6>Rating</h6>
								<p className="idg-v2-value">{data.rating} / 5</p>
								{data.user_ratings_total && (
									<p className="idg-v2-subtitle">{data.user_ratings_total.toLocaleString()} reviews</p>
								)}
							</div>
						</div>
					)}

					{/* Status */}
					{data.open_now !== undefined && (
						<div className="idg-v2-card">
							<div className="idg-v2-icon">
								{data.open_now ? <Check size={18} className="text-success" /> : <X size={18} className="text-danger" />}
							</div>
							<div className="idg-v2-content">
								<h6>Status</h6>
								<p className="idg-v2-value">
									{data.open_now
										? 'Open'
										: (data.business_status && data.business_status !== 'OPERATIONAL'
											? 'Permanently closed'
											: 'Closed')}
								</p>
							</div>
						</div>
					)}

				{/* View ID Info */}
				<a href="#id-info" className="idg-v2-card idg-v2-card-button">
					<div className="idg-v2-icon"><Info size={18} /></div>
					<div className="idg-v2-content">
						<h6 className="idg-v2-link">View Google ID info</h6>
					</div>
				</a>

				{/* Search Again */}
				<div className="idg-v2-card idg-v2-card-button" onClick={onReset}>
					<div className="idg-v2-icon"><RotateCw size={18} /></div>
					<div className="idg-v2-content">
						<h6 className="idg-v2-link">Search again</h6>
					</div>
				</div>

				{/* Website */}
				{data.website && (
					<a href={data.website} target="_blank" rel="noopener noreferrer" className="idg-v2-card idg-v2-card-button">
						<div className="idg-v2-icon"><Globe size={18} /></div>
						<div className="idg-v2-content">
							<h6 className="idg-v2-link">Visit the website</h6>
						</div>
					</a>
				)}

				{/* Google Maps Link */}
				{data.links?.maps && (
					<a href={data.links.maps} target="_blank" rel="noopener noreferrer" className="idg-v2-card idg-v2-card-button">
						<div className="idg-v2-icon"><MapPin size={18} /></div>
						<div className="idg-v2-content">
							<h6 className="idg-v2-link">View on Google Maps</h6>
						</div>
					</a>
				)}









				</div>
			</div>
		);
	}

	// If showing details only (hours, types, photos - below map)
	if (showDetailsOnly) {
		return (
			<div className="idg-places-v2-info">
				{/* Three column layout for hours, categories and photos */}
				<div className="idg-v2-three-column">
					{/* Opening Hours - prefer current hours (includes holidays) over regular hours */}
					{((data.current_opening_hours && data.current_opening_hours.weekday_text) ||
					  (data.opening_hours && data.opening_hours.weekday_text)) && (
						<div className="idg-v2-hours">
							<h6><Clock size={16} />Opening hours</h6>
							<div className="idg-v2-hours-grid">
								{(data.current_opening_hours?.weekday_text || data.opening_hours?.weekday_text).map((day, index) => (
									<div key={index} className="idg-v2-hour-item">
										{day}
									</div>
								))}
							</div>
						</div>
					)}

					{/* Business Types */}
					{(data.primary_type_display_name || (data.types && data.types.length > 0)) && (
						<div className="idg-v2-types">
							<h6><Tags size={16} />Categories</h6>
							<div className="idg-v2-types-list">
								{data.primary_type_display_name && (
									<span className="idg-v2-type-badge" style={{backgroundColor: '#0fd03b', color: '#fff', fontWeight: '700'}}>
										{data.primary_type_display_name}
									</span>
								)}
								{data.types && data.types.filter(type => type !== data.primary_type).map((type, index) => (
									<span key={index} className="idg-v2-type-badge">
										{type.replace(/_/g, ' ')}
									</span>
								))}
							</div>
						</div>
					)}

					{/* Photos */}
					{data.photos && data.photos.length > 0 && (
						<div className="idg-v2-photos">
							<h6><Camera size={16} />Photos</h6>
							<div className="idg-v2-photos-grid">
								{data.photos.slice(0, 6).map((photo, index) => (
									<div key={index} className="idg-v2-photo-thumb">
										<img src={photo.url} alt={`Business photo ${index + 1}`} />
									</div>
								))}
							</div>
							{data.photos.length > 6 && data.google_maps_url && (
								<a
									href={data.google_maps_url}
									target="_blank"
									rel="noopener noreferrer"
									className="idg-v2-view-more"
								>
									View all the photos on Google Maps <ExternalLink size={14} />
								</a>
							)}
						</div>
					)}
				</div>
			</div>
		);
	}

	// Default: show everything (for backwards compatibility)
	return (
		<div className="idg-places-v2-info">
			<div className="idg-places-v2-grid">

				{/* View ID Info */}
				<a href="#id-info" className="idg-v2-card idg-v2-card-button">
					<div className="idg-v2-icon"><Info size={18} /></div>
					<div className="idg-v2-content">
						<h6>View Google ID Info</h6>
						<p className="idg-v2-link">View details</p>
					</div>
				</a>

				{/* Search Again */}
				<div className="idg-v2-card idg-v2-card-button" onClick={onReset}>
					<div className="idg-v2-icon"><RotateCw size={18} /></div>
					<div className="idg-v2-content">
						<h6>Search Again</h6>
						<p className="idg-v2-link">New search</p>
					</div>
				</div>

				{/* Website */}
				{data.website && (
					<a href={data.website} target="_blank" rel="noopener noreferrer" className="idg-v2-card idg-v2-card-button">
						<div className="idg-v2-icon"><Globe size={18} /></div>
						<div className="idg-v2-content">
							<h6>Website</h6>
							<p className="idg-v2-link">Visit website</p>
						</div>
					</a>
				)}

				{/* Google Maps Link */}
				{data.links?.maps && (
					<a href={data.links.maps} target="_blank" rel="noopener noreferrer" className="idg-v2-card idg-v2-card-button">
						<div className="idg-v2-icon"><MapPin size={18} /></div>
						<div className="idg-v2-content">
							<h6>Google Maps</h6>
							<p className="idg-v2-link">View on Maps</p>
						</div>
					</a>
				)}

				{/* Rating */}
				{data.rating && (
					<div className="idg-v2-card">
						<div className="idg-v2-icon"><Star size={18} /></div>
						<div className="idg-v2-content">
							<h6>Rating</h6>
							<p className="idg-v2-value">{data.rating} / 5</p>
							{data.user_ratings_total && (
								<p className="idg-v2-subtitle">{data.user_ratings_total.toLocaleString()} reviews</p>
							)}
						</div>
					</div>
				)}

				{/* Status */}
				{data.open_now !== undefined && (
					<div className="idg-v2-card">
						<div className="idg-v2-icon">
							{data.open_now ? <Check size={18} className="text-success" /> : <X size={18} className="text-danger" />}
						</div>
						<div className="idg-v2-content">
							<h6>Status</h6>
							<p className="idg-v2-value">
								{data.open_now
									? 'Open'
									: (data.business_status && data.business_status !== 'OPERATIONAL'
										? 'Permanently closed'
										: 'Closed')}
							</p>
						</div>
					</div>
				)}

			</div>

			{/* Opening Hours - prefer current hours (includes holidays) over regular hours */}
			{((data.current_opening_hours && data.current_opening_hours.weekday_text) ||
			  (data.opening_hours && data.opening_hours.weekday_text)) && (
				<div className="idg-v2-hours">
					<h6><Clock size={16} /> Opening Hours</h6>
					<div className="idg-v2-hours-grid">
						{(data.current_opening_hours?.weekday_text || data.opening_hours?.weekday_text).map((day, index) => (
							<div key={index} className="idg-v2-hour-item">
								{day}
							</div>
						))}
					</div>
				</div>
			)}

			{/* Business Types */}
			{(data.primary_type_display_name || (data.types && data.types.length > 0)) && (
				<div className="idg-v2-types">
					<h6><Tags size={16} /> Categories</h6>
					<div className="idg-v2-types-list">
						{data.primary_type_display_name && (
							<span className="idg-v2-type-badge" style={{backgroundColor: '#0fd03b', color: '#fff', fontWeight: '700'}}>
								{data.primary_type_display_name}
							</span>
						)}
						{data.types && data.types.filter(type => type !== data.primary_type).map((type, index) => (
							<span key={index} className="idg-v2-type-badge">
								{type.replace(/_/g, ' ')}
							</span>
						))}
					</div>
				</div>
			)}

			{/* Photos */}
			{data.photos && data.photos.length > 0 && (
				<div className="idg-v2-photos">
					<h6><Camera size={16} /> Photos</h6>
					<div className="idg-v2-photos-grid">
						{data.photos.slice(0, 3).map((photo, index) => (
							<div key={index} className="idg-v2-photo-thumb">
								<img src={photo.url} alt={`Business photo ${index + 1}`} />
							</div>
						))}
					</div>
					{data.photos.length > 3 && data.google_maps_url && (
						<a
							href={data.google_maps_url}
							target="_blank"
							rel="noopener noreferrer"
							className="idg-v2-view-more"
						>
							View all {data.photos.length} photos on Google Maps <ExternalLink size={14} />
						</a>
					)}
				</div>
			)}
		</div>
	);
};

export default PlacesV2Info;
